import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Callout, Card, CardBody } from '../components/ui.jsx';
import BrandMark from '../components/BrandMark.jsx';
import { Download, Phone, ShieldCheck, Smartphone } from 'lucide-react';

const PHONE = '1-855-232-7996';
const PHONE_HREF = 'tel:+18552327996';
const SUPPORT = 'support@3lumpers.com';

/** The Android file, uploaded with the site (see public/downloads). */
const APK = '/downloads/3lumpers-android.apk';
const APK_NAME = '3Lumpers.apk';
const APK_SIZE = 'about 51 MB';

/**
 * How somebody gets the app while it is not in the Play Store.
 *
 * Written for a man holding a phone who has never installed anything from
 * outside the store: what to tap, what the warnings mean, and who to call.
 */
const STEPS = [
  [
    'Tap the download button',
    `The file is ${APK_SIZE}. Chrome will say it is not from the Play Store — that is expected, it came from us.`,
  ],
  [
    'Let it finish',
    'It lands in your Downloads. If your phone asks whether to keep the file, say yes.',
  ],
  [
    'Open the file and tap Install',
    'Android may say installs from this source are blocked. Tap Settings on that message, allow it for Chrome, then tap Install again. Play Protect may warn about an unknown app — also normal for a file from outside the store.',
  ],
  [
    'Sign in',
    'Use the email and password the office set up for you. Never signed in before? Tap "Sign up to work" on the first screen.',
  ],
];

export default function GetTheApp() {
  useEffect(() => {
    document.title = 'Get the 3Lumpers app — 3Lumpers';
  }, []);

  return (
    <div className="landing">
      <div className="util-bar">
        <div className="util-bar-inner">
          <Link to="/" className="row gap-10">
            <BrandMark size={26} />
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 650 }}>3Lumpers</span>
          </Link>
          <div className="grow" />
          <a href={PHONE_HREF} className="row gap-6">
            <Phone size={13} /> {PHONE}
          </a>
        </div>
      </div>

      <div style={{ padding: '34px 18px 64px', maxWidth: 680, margin: '0 auto' }}>
        <span className="label">For workers</span>
        <h1 className="mt-8">Get the 3Lumpers app</h1>
        <p className="lede mt-12">
          Work comes to your phone: the pay, the pallets and how far it is before you say yes.
          One tap at the dock starts the clock, and Friday is payday.
        </p>

        <div className="row gap-12 mt-20 wrap">
          <a href={APK} download={APK_NAME}>
            <Button variant="primary" size="lg" icon={Download}>
              Download for Android
            </Button>
          </a>
          <a href={PHONE_HREF}>
            <Button variant="outline" size="lg" icon={Phone}>
              {PHONE}
            </Button>
          </a>
        </div>
        <p className="dim mt-12" style={{ fontSize: 'var(--fs-sm)' }}>
          Android phones · free · {APK_SIZE}. The iPhone app is not ready yet — see below.
        </p>

        <Card className="mt-28">
          <CardBody>
            <div className="row gap-10">
              <Smartphone size={16} className="amber" />
              <h3>Four taps and you are in</h3>
            </div>
            <div className="col gap-16 mt-16">
              {STEPS.map(([title, body], index) => (
                <div key={title} className="row-top gap-12">
                  <Badge tone="amber" size="sm">{index + 1}</Badge>
                  <div>
                    <div style={{ fontWeight: 650 }}>{title}</div>
                    <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        <Callout tone="blue" icon={ShieldCheck} title="Where the file comes from" className="mt-20">
          It is our own build, handed over by us, not by a store. Nothing on your phone is shared
          with anyone, and the app only uses your location to show jobs near you.
        </Callout>

        <Callout tone="amber" icon={Smartphone} title="Got an iPhone?" className="mt-20">
          Not yet. The app is built for iPhones but Apple has not signed us off to put it on one.
          Call <a href={PHONE_HREF}>{PHONE}</a> and we will tell you where that stands.
        </Callout>

        <p className="muted mt-20">
          Stuck at any point? Call <a href={PHONE_HREF}>{PHONE}</a>, 6am to 8pm daily, or email{' '}
          <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>. New here?{' '}
          <Link to="/signup?path=lumper" className="amber">Sign up to work</Link>.
        </p>
      </div>
    </div>
  );
}
